import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, Trash2, Loader2, Globe, X, ArrowRightLeft, Link as LinkIcon, MessageSquare } from "lucide-react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import type { CMA } from "@/hooks/useCMAData";

interface CMACardProps {
  cma: CMA;
  onUpdate?: () => void;
  hoverColor?: string;
  actionsTourId?: string;
}

export function CMACard({ cma, onUpdate, hoverColor = "#e8edf5", actionsTourId }: CMACardProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isConverting, setIsConverting] = useState(false);

  const fullAddress = [cma.address, cma.city, cma.state, cma.zip].filter(Boolean).join(", ");

  const pd = cma.property_data || {};
  const displayImage =
    cma.custom_property_image_url ||
    pd.hiResImageLink ||
    pd.desktopWebHdpImageLink ||
    pd.photos?.[0]?.mixedSources?.jpeg?.slice(-1)?.[0]?.url ||
    pd.photos?.[0]?.url ||
    pd.originalPhotos?.[0]?.mixedSources?.jpeg?.slice(-1)?.[0]?.url ||
    pd.originalPhotos?.[0]?.url ||
    pd.imgSrc ||
    null;
  const agentName = cma.custom_agent_name;
  const agentPhoto = cma.custom_agent_headshot_url;
  const agentInitials = agentName
    ? agentName.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()
    : "";

  const priceRange =
    cma.suggested_price_low && cma.suggested_price_high
      ? `$${(cma.suggested_price_low / 1000).toFixed(0)}K–$${(cma.suggested_price_high / 1000).toFixed(0)}K`
      : null;

  const status = cma.converted_north_id ? "converted" : cma.is_published ? "published" : "draft";
  const hasFeedback = !!cma.seller_feedback_sentiment;

  const handlePublishToggle = async () => {
    setIsPublishing(true);
    try {
      const { error } = await supabase
        .from("cmas")
        .update({
          is_published: !cma.is_published,
          published_at: !cma.is_published ? new Date().toISOString() : null,
        })
        .eq("id", cma.id);
      if (error) throw error;
      toast({ title: cma.is_published ? "CMA Unpublished" : "CMA Published" });
      onUpdate?.();
    } catch {
      toast({ title: "Error", description: "Failed to update publish status", variant: "destructive" });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCopyLink = async () => {
    if (!cma.is_published) {
      toast({ title: "Not published", description: "Publish the CMA first", variant: "destructive" });
      return;
    }
    const url = `${window.location.origin}/cma/public/${cma.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast({ title: "Link copied!" });
    } catch {
      toast({ title: "Error", description: "Failed to copy link", variant: "destructive" });
    }
  };

  const handleConvert = async () => {
    if (cma.converted_north_id) {
      navigate(`/seller-report/${cma.converted_north_id}`);
      return;
    }
    setIsConverting(true);
    try {
      const { data, error } = await supabase.functions.invoke("convert-cma-to-north", {
        body: { cmaId: cma.id },
      });
      if (error) throw error;
      toast({ title: "Converted!", description: "CMA activated as North Report" });
      onUpdate?.();
      navigate(`/seller-report/${data.northId}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Conversion failed";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsConverting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteConfirmText.toLowerCase() !== "delete") {
      toast({ title: "Error", description: "Type 'delete' to confirm", variant: "destructive" });
      return;
    }
    setIsDeleting(true);
    try {
      const { error } = await supabase.from("cmas").delete().eq("id", cma.id);
      if (error) throw error;
      toast({ title: "CMA deleted" });
      onUpdate?.();
    } catch {
      toast({ title: "Error", description: "Failed to delete CMA", variant: "destructive" });
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
      setDeleteConfirmText("");
    }
  };

  const createdDate = new Date(cma.created_at).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return (
    <>
      <AlertDialog
        open={isDeleteOpen}
        onOpenChange={(open) => {
          setIsDeleteOpen(open);
          if (!open) setDeleteConfirmText("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this CMA?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this CMA. This action cannot be undone.
              <div className="mt-4">
                <Label htmlFor="cmaCardDeleteConfirm">Type "delete" to confirm:</Label>
                <Input
                  id="cmaCardDeleteConfirm"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="delete"
                  className="mt-2"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting || deleteConfirmText.toLowerCase() !== "delete"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="bg-card border border-border rounded-xl overflow-hidden flex flex-col group hover:shadow-md transition-shadow duration-200">
        {/* Header: custom upload → Zillow photo → gradient fallback */}
        {displayImage ? (
          <div className="relative aspect-[16/9] overflow-hidden bg-muted flex-shrink-0">
            <img
              src={displayImage}
              alt={fullAddress}
              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
            />
            <div className="absolute top-2.5 left-2.5">
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase bg-black/55 text-white px-2 py-1 rounded-md backdrop-blur-sm">
                CMA
              </span>
            </div>
            {hasFeedback && (
              <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-emerald-700/75 text-white text-xs px-2 py-1 rounded-md backdrop-blur-sm">
                <MessageSquare className="h-3 w-3" />
                <span>Feedback</span>
              </div>
            )}
            {priceRange && (
              <div className="absolute bottom-2.5 left-2.5 bg-black/55 text-white text-sm font-bold px-2.5 py-1 rounded-md backdrop-blur-sm">
                {priceRange}
              </div>
            )}
          </div>
        ) : (
          <div className="relative h-28 bg-gradient-to-br from-indigo-500/15 via-violet-500/10 to-blue-500/10 flex items-end p-3.5 flex-shrink-0">
            <div className="absolute top-2.5 left-2.5">
              <span className="text-[10px] font-semibold tracking-[0.15em] uppercase bg-indigo-700/75 text-white px-2 py-1 rounded-md backdrop-blur-sm">
                CMA
              </span>
            </div>
            {hasFeedback && (
              <div className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-emerald-700/75 text-white text-xs px-2 py-1 rounded-md backdrop-blur-sm">
                <MessageSquare className="h-3 w-3" />
                <span>Feedback</span>
              </div>
            )}
            {priceRange && (
              <p className="text-xl font-bold text-foreground/80 leading-none">{priceRange}</p>
            )}
          </div>
        )}

        {/* Card body */}
        <div className="p-4 flex-1 flex flex-col gap-1.5 min-h-0">
          <Link
            to={`/cma/${cma.id}`}
            className="font-semibold text-sm text-foreground hover:text-primary hover:underline leading-snug line-clamp-2"
          >
            {fullAddress}
          </Link>

          <p className="text-xs text-muted-foreground truncate">
            {cma.seller_name ? `For: ${cma.seller_name}` : <span className="invisible">—</span>}
          </p>

          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            {status === "converted" ? (
              <Badge variant="default" className="gap-1 bg-blue-600 text-white text-xs">
                <ArrowRightLeft className="h-3 w-3" />
                Converted
              </Badge>
            ) : status === "published" ? (
              <Badge variant="default" className="gap-1 bg-green-600 text-white text-xs">
                <Globe className="h-3 w-3" />
                Published
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 text-xs">
                Draft
              </Badge>
            )}
          </div>

          <div className="flex items-center gap-1.5 mt-auto pt-1.5">
            {agentPhoto ? (
              <img src={agentPhoto} alt={agentName || ""} className="h-5 w-5 rounded-full object-cover flex-shrink-0" />
            ) : agentInitials ? (
              <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <span className="text-[9px] font-semibold text-muted-foreground">{agentInitials}</span>
              </div>
            ) : null}
            {agentName && (
              <span className="text-xs text-muted-foreground truncate flex-1">{agentName}</span>
            )}
            <span className="text-xs text-muted-foreground/70 flex-shrink-0 ml-auto">{createdDate}</span>
          </div>
        </div>

        {/* Action footer */}
        <div className="px-3 py-2 border-t border-border flex items-center gap-0.5" {...(actionsTourId ? { 'data-tour': actionsTourId } : {})}>
          <Link
            to={`/cma/${cma.id}`}
            title="View CMA"
            className="inline-flex items-center gap-1.5 flex-1 justify-center h-8 rounded-md text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = hoverColor; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
          >
            <Eye className="h-3.5 w-3.5" />
            View
          </Link>
          <div className="w-px h-4 bg-border mx-0.5" />
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={handlePublishToggle}
            disabled={isPublishing}
            title={cma.is_published ? "Unpublish" : "Publish"}
          >
            {isPublishing ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : cma.is_published ? (
              <Globe className="h-3.5 w-3.5 text-green-600" />
            ) : (
              <div className="relative inline-flex items-center justify-center h-3.5 w-3.5">
                <Globe className="h-3.5 w-3.5" />
                <X className="h-3.5 w-3.5 absolute rotate-45" />
              </div>
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={handleCopyLink}
            disabled={!cma.is_published}
            title={cma.is_published ? "Copy link" : "Not published"}
          >
            <LinkIcon className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground"
            onClick={handleConvert}
            disabled={isConverting}
            title={cma.converted_north_id ? "View North Report" : "Activate as North Report"}
          >
            {isConverting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ArrowRightLeft className="h-3.5 w-3.5" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setIsDeleteOpen(true)}
            title="Delete CMA"
          >
            <Trash2 className="h-3.5 w-3.5 text-destructive" />
          </Button>
        </div>
      </div>
    </>
  );
}
